import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Avatar,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch } from "react-redux";
import { createDirector, updateDirector } from "redux/actions";
import { Director } from "types/models/investorDeskMaster/InvestorDeskMasterApp";

interface DirectorDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  director?: Director | null;
}

const DirectorDialog: React.FC<DirectorDialogProps> = ({
  open,
  onClose,
  onSuccess,
  director,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isEditMode = Boolean(director && director.id);

  const [name, setName] = useState("");
  const [post, setPost] = useState("");
  const [sequence, setSequence] = useState<number | string>(0);
  const [isDirector, setIsDirector] = useState(false);
  const [description, setDescription] = useState("");
  const [experience, setExperience] = useState("");
  const [associatedSince, setAssociatedSince] = useState("");
  const [qualifications, setQualifications] = useState("");
  const [earlierAssociations, setEarlierAssociations] = useState("");

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (open) {
      if (director) {
        setName(director.name || "");
        setPost(director.post || "");
        setSequence(director.sequence !== undefined ? director.sequence : 0);
        setIsDirector(Boolean(director.is_director));
        setDescription(director.description || "");
        setExperience(director.experience || "");
        setAssociatedSince(director.associated_since || "");
        setQualifications(director.qualifications || "");
        setEarlierAssociations(director.earlier_associations || "");
        setPreviewUrl(director.photo_url || director.photo || null);
      } else {
        setName("");
        setPost("");
        setSequence(0);
        setIsDirector(false);
        setDescription("");
        setExperience("");
        setAssociatedSince("");
        setQualifications("");
        setEarlierAssociations("");
        setPreviewUrl(null);
      }
      setSelectedFile(null);
      setErrorMessage("");
    }
  }, [open, director]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
      if (!validTypes.includes(file.type)) {
        setErrorMessage("Please select a valid image file (JPEG, PNG, GIF, WEBP)");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setErrorMessage("Image size must be less than 10MB");
        return;
      }
      setErrorMessage("");
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleRemovePhoto = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setErrorMessage("Name is required");
      return;
    }
    if (!post.trim()) {
      setErrorMessage("Designation is required");
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage("");

      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("post", post.trim());
      formData.append("sequence", String(sequence));
      formData.append("is_director", String(isDirector));
      formData.append("description", description.trim());
      formData.append("experience", experience.trim());
      formData.append("associated_since", associatedSince.trim());
      formData.append("qualifications", qualifications.trim());
      formData.append("earlier_associations", earlierAssociations.trim());

      if (selectedFile) {
        formData.append("photo", selectedFile);
      } else if (!previewUrl && isEditMode) {
        formData.append("photo", "");
      }

      if (isEditMode && director) {
        await (dispatch as any)(updateDirector(director.id, formData));
      } else {
        await (dispatch as any)(createDirector(formData));
      }

      setIsLoading(false);
      onSuccess();
      onClose();
    } catch (err: any) {
      setIsLoading(false);
      setErrorMessage(err?.response?.data?.error || err?.message || "Failed to save team member");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isEditMode ? <EditIcon color="primary" /> : <AddIcon color="primary" />}
          <Typography variant="h6" fontWeight="bold">
            {isEditMode ? "Edit Team Member" : "Add Team Member"}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={isLoading}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
            {errorMessage}
          </Alert>
        )}

        {/* Photo Upload Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 3,
            gap: 1.5,
          }}
        >
          <Box sx={{ position: "relative" }}>
            <Avatar
              src={previewUrl || undefined}
              alt={name || "Member"}
              sx={{
                width: 110,
                height: 110,
                border: "3px solid #e0e0e0",
                boxShadow: 2,
                fontSize: 40,
                bgcolor: "primary.main",
              }}
            >
              {!previewUrl && (name ? name.charAt(0).toUpperCase() : "?")}
            </Avatar>
            {previewUrl && (
              <IconButton
                size="small"
                onClick={handleRemovePhoto}
                sx={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  bgcolor: "error.main",
                  color: "white",
                  "&:hover": { bgcolor: "error.dark" },
                  width: 26,
                  height: 26,
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          <Button
            variant="outlined"
            size="small"
            startIcon={<PhotoCameraIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            {previewUrl ? "Change Photo" : "Upload Photo"}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Allowed: JPEG, PNG, WEBP (Max 10MB)
          </Typography>
        </Box>

        {/* Form Fields */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            placeholder="e.g. Pankaj Nagpal"
            size="small"
          />

          <TextField
            label="Designation / Post"
            value={post}
            onChange={(e) => setPost(e.target.value)}
            required
            fullWidth
            placeholder="e.g. Executive Director or Operations Head"
            size="small"
          />

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              label="Sort Sequence"
              type="number"
              value={sequence}
              onChange={(e) => setSequence(e.target.value)}
              size="small"
              sx={{ width: 140 }}
              helperText="Display order"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isDirector}
                  onChange={(e) => setIsDirector(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" fontWeight="medium">
                  {isDirector ? "Director (Top Row)" : "Department Head (Bottom Row)"}
                </Typography>
              }
              sx={{ ml: 1 }}
            />
          </Box>

          <TextField
            label="Short Bio / Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            fullWidth
            placeholder="Brief description or intro..."
            size="small"
          />

          {/* Optional Detailed Fields */}
          <Accordion variant="outlined" sx={{ borderRadius: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight="bold">
                Additional Details (Website Modal)
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Experience"
                value={experience}
                onChange={(e) => setExperience(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="e.g. 21 years leadership, managerial, expansion..."
                size="small"
              />

              <TextField
                label="Associated Since"
                value={associatedSince}
                onChange={(e) => setAssociatedSince(e.target.value)}
                fullWidth
                placeholder="e.g. August 2024"
                size="small"
              />

              <TextField
                label="Qualifications"
                value={qualifications}
                onChange={(e) => setQualifications(e.target.value)}
                fullWidth
                placeholder="e.g. BE Electronics and Communication"
                size="small"
              />

              <TextField
                label="Earlier Associations"
                value={earlierAssociations}
                onChange={(e) => setEarlierAssociations(e.target.value)}
                fullWidth
                placeholder="e.g. Reliance Infocom, Airtel, VSNL"
                size="small"
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isLoading} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading || !name.trim() || !post.trim()}
          variant="contained"
          color="primary"
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {isLoading ? "Saving..." : isEditMode ? "Update" : "Add Member"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DirectorDialog;
